import {
  useIsFetching,
  useIsMutating,
  useQueryClient,
} from '@tanstack/react-query';
import { Button, Col, Divider, Form, Row } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import qs from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import LoadingOverlay from 'src/components/applications/LoadingOverlay';
import { Card } from 'src/components/custom';
import { API_KEY } from 'src/constants/apiKey';
import {
  FINISHED_PRODUCT_PARAMS,
  SCREEN_ID,
  STATUS_CODE,
  // SUGGEST_YOUKEN_TYPE,
} from 'src/constants/app';
import useQueryUrl from 'src/hooks/useQueryUrl';
import history from 'src/libs/history';
import { PATH as PATH_ORDER } from 'src/pages/order';
import { PATH as PATH_QUOTATION } from 'src/pages/quotation';
import useEstimatePrice from 'src/queries/useEstimatePrice';
import usePriceFractions from 'src/queries/usePriceFractions';
import useSettingShowProducts from 'src/queries/useSettingShowProducts';
import { productFormat } from 'src/utils/product';
import { formatSizes, getVisiableRollEyes } from 'src/utils/quotation';
import useForm from './useForm';

import { AxiosResponse } from 'axios';
import useProductInformation from 'src/queries/useProductInformation';
import quotation from 'src/repositories/quotation';
import LocalStorage from 'src/utils/LocalStorage';
import {
  checkIsProductFinished,
  convertFinishedSize,
  findOriginSize,
  findOriginSizeByApiList,
  getProductName,
  isImportedProductOnly,
  isMachiningMethodFromH2ToH5,
  checkChangeLanguage,
} from 'src/utils/helper';
import { getApiSeqnoFromToleranceCodes } from 'src/utils/tolerance';
import { I18n } from 'src/libs/i18n';
import CalculateResultTable from './CalculateResultTable';
import CategoriesList from './CategoriesList';
import CustomerInfo from './CustomerInfo';
import ErrorMessage from './ErrorMessage';
import InputOptional from './InputOptional';
import MachiningMethodList from './MachiningMethodList';
import ManuFacturingSize from './ManuFacturingSize';
import ProductList from './ProductList';
import SizeInput from './SizeInput';
import TrialNote from './TrialNote';
import CustomerProfile from './CustomerProfile';
import IndustryAndRegion from './IndustryAndRegion';

type Props = {
  isTrialMode?: boolean;
};

const Pricing: React.FC<Props> = ({ isTrialMode }) => {
  const [loading, setLoading] = useState(false);
  const [timerLoading, setTimerLoading] = useState<any>();
  const [product, setProduct] = useState('');
  const [estimateErrors, setEstimateErrors] = useState<string[]>([]);
  const [stockError, setStockError] = useState('');
  const [machiningMethod, setMachiningMethod] = useState('');
  const [manufacturingSizes, setManufacturingSizes] = useState<any>();
  const [productWidthData, setProductWidthData] = useState<any>();
  const [productLengthData, setProductLengthData] = useState<any>();
  const [sizes2, setSizes2] = useState<String>();
  const [sizes3, setSizes3] = useState<String>();
  const [productDetails, setProductDetails] = useState<any>();
  const [visibleRollEyes, setVisibleRollEyes] = useState(false);
  const [toleranceOptions, setToleranceOptions] = useState<any>({});
  const [dataCategoryPage, setDataCategoryPage] =
    useState<SearchParams.CategoryProductSearch>();
  const [labelError, setLabelError] =
    useState<Response.ManufacturingSize['item']>();
  const [isResetMachiningTolerance, setIsResetMachiningTolerance] =
    useState<boolean>(false);
  const [chamferMethod, setChamferMethod] =
    useState<Response.ChamferItem | null>(null);
  const [searchKeywordProduct, setSearchKeywordProduct] = useState<string>('');
  const [decimalInputSizeFlags, setDecimalInputSizeFlags] =
    useState<Payload.DecimalInputSizeFlags>();
  const [showMachiningTolerance, setShowMachiningTolerance] =
    useState<boolean>(false);

  const { t } = useTranslation();
  const client = useQueryClient();
  const params = useQueryUrl<SearchParams.EstimatePriceSearch>();
  const location = useLocation();
  const { pathname } = history.location;

  const { formInfo } = useForm(
    labelError,
    isTrialMode,
    isImportedProductOnly({
      product: product,
      checkZ0: true,
      method: machiningMethod,
    }),
  );
  const { handleSubmit, watch, getValues, setError, setValue, clearErrors } =
    formInfo;

  const varietyCode = watch('variety');
  const isProductFinished = checkIsProductFinished(varietyCode);

  const state = location?.state as any;

  const apiPathCurrentCurrencyUnit = '/currency/current-unit';
  const apiPathCurrencyTransfers = '/currency/transfers';
  const apiPathPriceApiList = '/price/api-list';

  const { estimatePrice, estimateDetail, queryTaxes, queryProductDimension } =
    useEstimatePrice({
      setError,
      labelError,
      isTrialMode,
      estimateId: params.webMitrNo,
      shhnCd: productDetails?.shhnCd,
      hakudoShhnCd: productDetails?.hakudoShhnCd,
      api1ShhnCd: productDetails?.api1ShhnCd,
      api2ShhnCd: productDetails?.api2ShhnCd,
      snpo1: productDetails?.snpo1,
    });

  const { queryFractions } = usePriceFractions();
  const { querySettingShowProducts } = useSettingShowProducts();

  const estimatePriceData = estimatePrice.data?.data?.item;
  const dataReEstimate = estimateDetail?.data?.data?.item;
  const dataProductDimension = queryProductDimension?.data?.data?.item;
  const fractionsData = queryFractions?.data?.data?.item?.fractions;
  const settingShowProductsData = querySettingShowProducts?.data?.data?.item;

  const [lang, setLang] = useState(LocalStorage.lang);
  const prevLang = useRef(lang);

  const [formValue, setFormValue] = useState({});
  const [enableBackOrder, setEnableBackOrder] = useState(false);

  const listProductId = {
    hakudoShhnCd: productFormat.splitProductId(product)?.hakudoShhnCd,
    shhnCd: productFormat.splitProductId(product)?.shhnCd,
    api1ShhnCd: productFormat.splitProductId(product)?.api1ShhnCd,
    api2ShhnCd: productFormat.splitProductId(product)?.api2ShhnCd,
  };

  const { productsInfor } = useProductInformation(
    listProductId,
    isProductFinished,
    setEstimateErrors,
    setStockError,
  );

  useEffect(() => {
    setLang(LocalStorage.lang);
  }, [LocalStorage.lang]);

  /**
   * re-estimate from order history page.
   * re fill value of variety from detail order
   */
  useEffect(() => {
    if (dataReEstimate) {
      !getValues('variety') && setValue('variety', dataReEstimate?.hinshL3Cd);
    }
  }, [dataReEstimate]);

  // reload init url query data can't get detail re-estimate.
  useEffect(() => {
    if (estimateDetail.isError) {
      history.replace({
        search: qs.stringify({
          hinshL3Cd: watch('variety'),
          keijoL3Cd: watch('shape'),
        }),
      });
    }
  }, [estimateDetail.isError]);

  /**
   * fill form estimate by data product from category page.
   * set default value variety, shape and productId
   */
  useEffect(() => {
    const dataCategoryPage = state?.state?.product;
    const dataProductDetails = state?.productDetails;

    if (dataCategoryPage) {
      setValue('variety', dataCategoryPage?.hinshL3Cd);
      setValue('shape', dataCategoryPage?.keijoL3Cd);
      setDataCategoryPage(dataCategoryPage);
    }

    if (dataProductDetails) {
      setProductDetails(state?.productDetails);
    }
  }, [state?.state]);

  useEffect(() => {
    if (!dataReEstimate) return;
    if (
      dataReEstimate.hinshL3Cd !== watch('variety') ||
      dataReEstimate.keijoL3Cd !== watch('shape')
    ) {
      setIsResetMachiningTolerance(true);
    }
  }, [dataReEstimate, watch('variety'), watch('shape')]);

  useEffect(() => {
    setVisibleRollEyes(
      getVisiableRollEyes(
        getValues('shape'),
        getValues('variety'),
        machiningMethod,
        product,
      ),
    );
  }, [watch('variety'), watch('shape'), machiningMethod, product]);

  useEffect(() => {
    if (params.webMitrNo && dataReEstimate)
      history.replace({
        search: qs.stringify(
          {
            hinshL3Cd: dataReEstimate?.hinshL3Cd,
            keijoL3Cd: dataReEstimate?.keijoL3Cd,
            webMitrNo: params.webMitrNo,
          },
          {
            skipNull: true,
            skipEmptyString: true,
          },
        ),
      });
  }, [params.webMitrNo, dataReEstimate]);

  useEffect(() => {
    if (checkChangeLanguage(prevLang?.current, lang)) {
      history.replace(pathname);
    }
  }, [lang]);

  const handleSwapWidthAndLength = (
    width,
    length,
    isSwappedS2AndS3,
    disableFractionS2AndS3,
  ) => {
    // swap value of productWidth and productLength
    if (
      manufacturingSizes?.swapS2AndS3 &&
      width &&
      length &&
      Number(width) > Number(length)
    ) {
      [width, length] = [length, width];
      setValue('product_width', width);
      setValue('product_length', length);
      if (
        decimalInputSizeFlags?.isSnpo2BunsuHyoji &&
        decimalInputSizeFlags?.isSnpo3BunsuHyoji
      ) {
        setProductWidthData(productLengthData);
        setProductLengthData(productWidthData);
        isSwappedS2AndS3 = true;
      } else {
        disableFractionS2AndS3 = true;
      }
    }
    return [width, length, isSwappedS2AndS3, disableFractionS2AndS3];
  };

  /**
   * Wait for convert size API to complete before calculation
   * @returns Promise<void>
   */
  const waitForConvertSizeCompletion = async (): Promise<void> => {
    const MAX_RETRIES = 30; // Maximum 30 attempts (3 seconds)
    const POLLING_INTERVAL = 100; // Check every 100ms

    let retryCount = 0;
    while (retryCount < MAX_RETRIES) {
      const isMutatingConvertSize = client.isMutating({
        mutationKey: [API_KEY.CONVERT_SIZE],
      });

      if (isMutatingConvertSize === 0) {
        // API has completed, proceed with calculation
        break;
      }

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL));
      retryCount++;
    }

    // If retry limit exceeded, log warning but continue (fail-safe)
    if (retryCount >= MAX_RETRIES) {
      console.warn(
        'Convert size API timeout - proceeding with calculation anyway',
      );
    }
  };

  const onHandleCalculate = async (val, reEstimateType?: string) => {
    // Wait for convert size API to complete before calculation
    await waitForConvertSizeCompletion();

    const priceApiListRes = await client.fetchQuery<
      AxiosResponse<Response.PriceApiList>
    >({
      queryKey: [apiPathPriceApiList],
      queryFn: quotation.getPriceApiList,
    });

    const priceApiList = priceApiListRes?.data?.items;

    // Determine preferred API seqno from tolerance codes
    const preferredApiSeqno = getApiSeqnoFromToleranceCodes(
      {
        thicknessCd: val?.machining_thickness,
        widthCd: val?.machining_width,
        lengthCd: val?.machining_length,
        outsideDiaCd: val?.machining_outside_diameter,
      },
      toleranceOptions,
      priceApiList,
    );

    const sizes1Data =
      findOriginSizeByApiList(
        manufacturingSizes?.sizes,
        val?.product_thickness,
        priceApiList,
        listProductId,
        product,
        preferredApiSeqno,
      ) || val?.product_thickness;
    const sizes2Data =
      findOriginSize(productWidthData, val?.product_width) || sizes2;
    const sizes3Data =
      findOriginSize(productLengthData, val?.product_length) || sizes3;
    let productWidthVal = isMachiningMethodFromH2ToH5(machiningMethod)
      ? sizes2Data
      : val?.product_width;
    let productLengthVal = isMachiningMethodFromH2ToH5(machiningMethod)
      ? sizes3Data
      : val?.product_length;
    let isSwappedS2AndS3 = false;
    let disableFractionS2AndS3 = false;

    [
      productWidthVal,
      productLengthVal,
      isSwappedS2AndS3,
      disableFractionS2AndS3,
    ] = handleSwapWidthAndLength(
      productWidthVal,
      productLengthVal,
      isSwappedS2AndS3,
      disableFractionS2AndS3,
    );

    // Get latest converted values from form instead of parameter val to avoid incorrect values during convert size API processing
    const latestConvertedThickness = getValues('converted_product_thickness');
    const latestConvertedWidth = getValues('converted_product_width');
    const latestConvertedLength = getValues('converted_product_length');

    const widthSize = productWidthVal || val?.product_width;
    const lengthSize = productLengthVal || val?.product_length;
    const convertedSize = formatSizes({
      thickness: latestConvertedThickness || val?.product_thickness,
      width: latestConvertedWidth || widthSize,
      length: latestConvertedLength || lengthSize,
    });
    const unitConvertedSize = formatSizes({
      thickness: manufacturingSizes?.unitSize1,
      width: manufacturingSizes?.size1Label && manufacturingSizes?.unitSize2,
      length: manufacturingSizes?.size2Label && manufacturingSizes?.unitSize3,
    });
    const inputSize = formatSizes({
      thickness: val?.product_thickness,
      width: widthSize,
      length: lengthSize,
    });

    const payload = {
      picName: val.customer_name,
      deliveryDestinationCd: val?.destination
        ? val?.destination.slice(0, 3)
        : val?.destinationItem?.rmOtokesaki?.otokesakiCd,
      hinshL3Cd: val?.variety,
      keijoL3Cd: val?.shape,
      hakudoShhnCd: productFormat.splitProductId(product)?.hakudoShhnCd,
      shhnCd: productFormat.splitProductId(product)?.shhnCd,
      api1ShhnCd: productFormat.splitProductId(product)?.api1ShhnCd,
      api2ShhnCd: productFormat.splitProductId(product)?.api2ShhnCd,
      hakudoShhnm: getProductName(val?.productItem, true),
      setdnKakoCd: isProductFinished
        ? FINISHED_PRODUCT_PARAMS.DEFAULT_SETDN_KAKO_CD
        : machiningMethod,
      setdnkako: isProductFinished
        ? FINISHED_PRODUCT_PARAMS.DEFAULT_SETDN_KAKO
        : val?.machiningItem?.label,
      snpoSize1: isProductFinished
        ? FINISHED_PRODUCT_PARAMS.DEFAULT_SNPO_SIZE1
        : latestConvertedThickness || val?.product_thickness,
      snpoSize2: latestConvertedWidth || productWidthVal,
      snpoSize3: latestConvertedLength || productLengthVal,
      finishingSize: isProductFinished
        ? FINISHED_PRODUCT_PARAMS.DEFAULT_FINISHING_SIZE
        : convertedSize,
      finishingSizeText: isProductFinished
        ? FINISHED_PRODUCT_PARAMS.DEFAULT_FINISHING_SIZE_TEXT
        : val?.sizeItem?.format,
      numberOfMember: parseInt(val?.number_product),
      thicknessToleranceLabel:
        val?.machining_thickness &&
        t('pricing_page.machining_tolerance.plate_thickness'),
      thicknessToleranceCd: val?.machining_thickness,
      widthToleranceLabel:
        val?.machining_width && t('pricing_page.machining_tolerance.width'),
      widthToleranceCd: val?.machining_width,
      lengthToleranceLabel:
        val?.machining_length && t('pricing_page.machining_tolerance.length'),
      lengthToleranceCd: val?.machining_length,
      outsideDiaToleranceLabel:
        val?.machining_outside_diameter &&
        t('pricing_page.machining_tolerance.outside_diameter'),
      outsideDiaToleranceCd: val?.machining_outside_diameter,
      insideDiaToleranceLabel:
        val?.machining_inside_diameter &&
        t('pricing_page.machining_tolerance.inside_diameter'),
      insideDiaToleranceCd: val?.machining_inside_diameter,
      chamferType: val?.options_size_unspecified,
      chamferC: val?.options_size_c,
      cornerA: val?.corner_option_a,
      cornerB: val?.corner_option_b,
      cornerC: val?.corner_option_c,
      cornerD: val?.corner_option_d,
      specifiedRollingDCd: val?.roll_eyes,
      millSheetCd: val?.mill_sheets,
      orderNumber: val?.order_number,
      remarks: val?.order_notes,
      millingPatternCd: val?.chamferItem?.fraiseKakoPtn,
      millingPatternName: val?.chamferItem?.fraiseKakoPtnMeis,
      keijoInfo: {
        keijoL1Cd: manufacturingSizes?.keijoInfo?.keijoL1Cd,
        keijoL2Cd: manufacturingSizes?.keijoInfo?.keijoL2Cd,
        keijoL3Cd: manufacturingSizes?.keijoInfo?.keijoL3Cd,
      },
      sizes1: isProductFinished
        ? FINISHED_PRODUCT_PARAMS.DEFAULT_SIZE1
        : sizes1Data,
      sizes2: sizes2Data,
      sizes3: sizes3Data,
      suggestYoukenType: reEstimateType,
      webMitrNoReEstimation: reEstimateType
        ? estimatePriceData?.webMitrNo
        : undefined,
      zaikoTaniKbn: !manufacturingSizes?.sizeMustConvert
        ? convertFinishedSize({
            size: inputSize,
            unitSize: unitConvertedSize,
            manufacturingSizes: manufacturingSizes,
            product: product,
            method: machiningMethod,
            inputValue: val,
          })
        : undefined,
      inputSunpo: manufacturingSizes?.sizeMustConvert
        ? convertFinishedSize({
            size: inputSize,
            unitSize: unitConvertedSize,
            manufacturingSizes: manufacturingSizes,
            product: product,
            method: machiningMethod,
            inputValue: val,
          })
        : undefined,
      sunpoTaniKubun: manufacturingSizes?.originSize,
      motoTaniKubun: manufacturingSizes?.motoTaniKubun,
      convertFlg: manufacturingSizes?.sizeMustConvert,
      unitSizeConvert: unitConvertedSize,
      seihnJoho: val?.seihnJoho,
      regScreenID: isTrialMode ? SCREEN_ID.R1003 : SCREEN_ID.R1004,
      isSwappedS2AndS3,
      disableFractionS2AndS3,
    };
    const dataOmit: any = _.omitBy(
      payload,
      (v) => _.isUndefined(v) || _.isNull(v) || v === '' || v === 0,
    );
    estimatePrice.mutate(dataOmit);
  };

  const isFetchingProducts = useIsFetching([API_KEY.PRODUCTS]);
  const isFetchingMachinings = useIsFetching([API_KEY.MACHINING_METHOD]);
  const isFetchingSizes = useIsFetching([API_KEY.MANUFACTURING_SIZES]);
  const isMutatingConvertSizes = useIsMutating([API_KEY.CONVERT_SIZE]);

  useEffect(() => {
    if (
      isFetchingProducts > 0 ||
      isFetchingMachinings > 0 ||
      isFetchingSizes > 0 ||
      isMutatingConvertSizes > 0
    ) {
      clearTimeout(timerLoading);
      setLoading(true);
      return;
    }
    setTimerLoading(
      setTimeout(() => {
        setLoading(false);
      }, 200),
    );
    return () => {
      clearTimeout(timerLoading);
    };
  }, [
    isFetchingProducts,
    isFetchingMachinings,
    isFetchingSizes,
    isMutatingConvertSizes,
  ]);

  // Check for machining range limit errors.
  const checkErrorParamsProcessingScope = (
    params,
    sizeItem,
    labelErrorProcessingRange,
  ) => {
    const snpoSize = sizeItem?.snpoSize;
    params?.forEach((key) => {
      switch (Number(key)) {
        case snpoSize - 1:
          return setError('product_thickness', {
            type: 'custom',
            message: t('message.E000091', {
              0: labelError?.head,
              1: labelErrorProcessingRange[0],
            }),
          });
        case snpoSize:
          return setError('product_width', {
            type: 'custom',
            message: t('message.E000091', {
              0: labelError?.size1Label,
              1: labelErrorProcessingRange[1],
            }),
          });
        case snpoSize + 1:
          return setError('product_length', {
            type: 'custom',
            message: t('message.E000091', {
              0: labelError?.size2Label,
              1: labelErrorProcessingRange[2],
            }),
          });

        default:
          break;
      }
    });
  };

  useEffect(() => {
    const errorCode = estimatePrice?.error?.response?.data?.code;
    if (errorCode) {
      if (errorCode === STATUS_CODE.OUT_OF_MACHINING_SIZE_RANGE_ERROR) {
        const showProcessingRange = getValues('showProcessingRange') || [];
        const errorData: any = estimatePrice?.error?.response?.data;
        checkErrorParamsProcessingScope(
          errorData?.params,
          labelError,
          showProcessingRange,
        );
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      switch (true) {
        case errorCode === STATUS_CODE.ESTIMATE_PRICE_ERROR:
          return setEstimateErrors([t('message.E000079')]);
        case errorCode === STATUS_CODE.ESTIMATE_STOCK_ERROR:
          return setStockError(t('message.E000048'));
        case errorCode?.includes(','): {
          //code: "001,002,003" | 001 -> 025
          const errorMessages =
            errorCode
              ?.split(',')
              .map((code) => {
                if (code >= '001' && code <= '025') {
                  return I18n.t(`message.${code}`);
                } else {
                  return '';
                }
              })
              .filter((message) => message !== '') || [];

          if (errorMessages?.length === 0) {
            //if code > 025 => show E000079
            errorMessages?.push(I18n.t('message.E000079'));
          }
          return setEstimateErrors(errorMessages);
        }
        case errorCode >= '001' && errorCode <= '025':
          return setEstimateErrors([I18n.t(`message.${errorCode}`)]);
        default: {
          return setEstimateErrors([t('message.E000079')]);
        }
      }
    }
    setEstimateErrors([]);
    setStockError('');
  }, [estimatePrice?.error]);

  const checkCurrencyTransfer = async (data) => {
    const currentCurrencyRes = await client.fetchQuery<
      AxiosResponse<Response.CurrentCurrencyUnit>
    >({
      queryKey: [apiPathCurrentCurrencyUnit],
      queryFn: quotation.getCurrentCurrencyUnit,
    });

    const currencyTransfersRes = await client.fetchQuery<
      AxiosResponse<Response.CurrencyTransfers>
    >({
      queryKey: [apiPathCurrencyTransfers],
      queryFn: quotation.getCurrencyTransfers,
    });

    const currentCurrency = currentCurrencyRes?.data?.item;
    const currencyTransfers = currencyTransfersRes?.data?.items;
    const currency = data?.totalPrice?.split(' ')[1];

    if (currentCurrency && currencyTransfers) {
      const currentCurrencyTransfers = currencyTransfers
        ?.filter((item) => item.kawaseKbCdTo === currentCurrency?.currentKawase)
        ?.map((currency) => currency.kawaseKbCdFromSymbol)
        ?.concat(currentCurrency.currentKawaseSymbol);
      return currentCurrencyTransfers?.includes(currency);
    }
  };

  const onHandleOrder = async (data) => {
    if (await checkCurrencyTransfer(data)) {
      history.push(PATH_ORDER, { items: [data] });
    } else {
      window.scrollTo({ top: 200, behavior: 'smooth' });
      setEstimateErrors([t('message.E000079')]);
    }
  };

  useEffect(() => {
    if (dataProductDimension) {
      setValue('variety', dataProductDimension?.hinshL3Cd);
      setValue('shape', dataProductDimension?.keijoL3Cd);
    }
  }, [dataProductDimension]);

  const resetInputData = () => {
    setValue('product_thickness', '');
    clearErrors('product_thickness');
    setValue('product_width', '');
    clearErrors('product_width');
    setValue('product_length', '');
    clearErrors('product_length');
    setValue('number_product', '');
    clearErrors('number_product');
    setValue('seihnJoho', undefined);
    clearErrors('seihnJoho');
    setValue('machining_thickness', '');
    setValue('machining_width', '');
    setValue('machining_length', '');
    setValue('machining_outside_diameter', '');
    setValue('machining_inside_diameter', '');
    setValue('options_size_unspecified', '');
    setValue('options_size_c', '');
    setValue('corner_option_a', '');
    setValue('corner_option_b', '');
    setValue('corner_option_c', '');
    setValue('corner_option_d', '');
    setValue('roll_eyes', '');
    setValue('mill_sheets', '');
    setValue('order_number', '');
    setValue('order_notes', '');
    setSizes2('');
    setSizes3('');
    setToleranceOptions({});
    setProductWidthData(undefined);
    setProductLengthData(undefined);
    setEstimateErrors([]);
    setStockError('');
  };

  useEffect(() => {
    if (estimatePriceData?.clearTolerance) {
      setValue('machining_thickness', '');
      setValue('machining_width', '');
      setValue('machining_length', '');
      setValue('machining_outside_diameter', '');
    }
  }, [estimatePriceData?.clearTolerance]);

  const ButtonSubmit = () => (
    <Button
      className="px-60 w-full"
      type="primary"
      loading={estimatePrice.isLoading}
      onClick={handleSubmit((val) => {
        setFormValue(val);
        setEnableBackOrder(false);
        onHandleCalculate(val);
      })}
    >
      {t('pricing_page.calculate_button')}
    </Button>
  );

  return (
    <div
      className={classNames(
        `content_pricing custom_content_pricing ${
          isTrialMode && 'content_pricing_for_guest'
        }`,
        {
          content_pricing_trial: isTrialMode,
        },
      )}
    >
      <Row className="justify-between" gutter={[15, 0]}>
        <Col span={16} xs={24} sm={24} md={24} lg={16} xl={16} xxl={16}>
          {/* {!isTrialMode && <Breadcrumb />} */}
          <Card
            title={
              isTrialMode
                ? t('pricing_page.page_trial_title')
                : t('pricing_page.page_title')
            }
            className={`content_pricing_card ${
              isTrialMode ? 'page_trial_title' : 'page_title'
            }`}
          >
            {stockError && <ErrorMessage content={stockError} />}
            {_.size(estimateErrors) > 0 &&
              estimateErrors?.map((error, index) => (
                <ErrorMessage key={index} content={error} />
              ))}
            <div className="content_pricing_content">
              {loading && <LoadingOverlay />}
              <Form
                onFinish={handleSubmit((val) => {
                  setFormValue(val);
                  setEnableBackOrder(false);
                  onHandleCalculate(val);
                })}
              >
                {!isTrialMode && (
                  <CustomerInfo
                    formInfo={formInfo}
                    dataReEstimate={dataReEstimate}
                    estimateId={params.webMitrNo}
                    prevLang={prevLang?.current}
                    lang={lang}
                  />
                )}
                <CategoriesList
                  formInfo={formInfo}
                  dataReEstimate={dataReEstimate}
                  dataCategoryPage={dataCategoryPage}
                  prevLang={prevLang?.current}
                  lang={lang}
                  isProductFinished={isProductFinished}
                  setSearchKeywordProduct={setSearchKeywordProduct}
                  resetInputData={resetInputData}
                  setProduct={setProduct}
                />
                <ProductList
                  setProduct={setProduct}
                  product={product}
                  formInfo={formInfo}
                  dataReEstimate={dataReEstimate}
                  dataCategoryPage={dataCategoryPage}
                  prevLang={prevLang?.current}
                  lang={lang}
                  dataProductDimension={dataProductDimension}
                  setIsResetMachiningTolerance={setIsResetMachiningTolerance}
                  setChamferMethod={setChamferMethod}
                  searchKeywordProduct={searchKeywordProduct}
                  setSearchKeywordProduct={setSearchKeywordProduct}
                  resetInputData={resetInputData}
                  settingShowProductsData={settingShowProductsData}
                />
                {!isProductFinished && (
                  <MachiningMethodList
                    product={product}
                    isFetchingMachinings={isFetchingMachinings}
                    machiningMethod={machiningMethod}
                    setMachiningMethod={setMachiningMethod}
                    formInfo={formInfo}
                    dataReEstimate={dataReEstimate}
                    dataProductDimension={dataProductDimension}
                    setIsResetMachiningTolerance={setIsResetMachiningTolerance}
                    chamferMethod={chamferMethod}
                    setChamferMethod={setChamferMethod}
                  />
                )}
                <ManuFacturingSize
                  product={product}
                  machiningMethod={machiningMethod}
                  formInfo={formInfo}
                  setLabelError={setLabelError}
                  setEstimateErrors={setEstimateErrors}
                  setSizes2={setSizes2}
                  setSizes3={setSizes3}
                  setStockError={setStockError}
                  dataReEstimate={dataReEstimate}
                  estimatePriceData={estimatePriceData}
                  setManufacturingSizes={setManufacturingSizes}
                  prevLang={prevLang?.current}
                  lang={lang}
                  shape={watch('shape')}
                  dataProductDimension={dataProductDimension}
                  isProductFinished={isProductFinished}
                  productFinishedInfor={productsInfor}
                  productLengthData={productLengthData}
                  productWidthData={productWidthData}
                  setProductLengthData={setProductLengthData}
                  setProductWidthData={setProductWidthData}
                  setToleranceOptions={setToleranceOptions}
                  fractionsData={fractionsData}
                  decimalInputSizeFlags={decimalInputSizeFlags}
                  setDecimalInputSizeFlags={setDecimalInputSizeFlags}
                  showMachiningTolerance={showMachiningTolerance}
                />
                {!isProductFinished && (
                  <SizeInput
                    machiningMethod={machiningMethod}
                    formInfo={formInfo}
                    dataReEstimate={dataReEstimate}
                    toleranceOptions={toleranceOptions}
                    product={product}
                    isResetMachiningTolerance={isResetMachiningTolerance}
                    showMachiningTolerance={showMachiningTolerance}
                    setShowMachiningTolerance={setShowMachiningTolerance}
                  />
                )}
                <InputOptional
                  formInfo={formInfo}
                  visibleRollEyes={visibleRollEyes}
                  isProductFinished={isProductFinished}
                />
                {isTrialMode && <IndustryAndRegion formInfo={formInfo} />}
                <Divider className="mg-vertical-20" />
              </Form>
            </div>
          </Card>
        </Col>
        <Col
          span={8}
          xs={24}
          sm={24}
          md={24}
          lg={8}
          xl={8}
          xxl={8}
          className={classNames('container_calculateResultTable', {
            has_estimate_price_data: estimatePriceData,
          })}
        >
          {estimatePriceData && (
            <React.Fragment>
              <CalculateResultTable
                estimatePriceData={estimatePriceData}
                queryTaxes={queryTaxes?.data?.data?.items}
                enableBackOrder={enableBackOrder}
                setEnableBackOrder={setEnableBackOrder}
                onHandleCalculate={onHandleCalculate}
                formValue={formValue}
                isProductFinished={isProductFinished}
              />
              {!enableBackOrder ? (
                (estimatePriceData?.hasCheap ||
                  estimatePriceData?.hasQuick) && (
                  <div className="re_estimate_container">
                    {/* <Divider className="form_divider" /> */}
                    {/* <Typography.Title
                      level={5}
                      className="re_estimate_condition_title title_re_estimate"
                    >
                      {t('pricing_page.re_estimate.note')}
                    </Typography.Title> */}
                    {/* <Row className="re_estimate custom_re_estimate">
                      {estimatePriceData?.hasCheap && (
                        <Col className="re_estimate_condition">
                          <Typography.Title
                            level={5}
                            className="re_estimate_condition_title"
                          >
                            {t('pricing_page.re_estimate.cheaper')}
                          </Typography.Title>
                          <Button
                            type="primary"
                            onClick={() => {
                              setEnableBackOrder(true);
                              onHandleCalculate(
                                formValue,
                                SUGGEST_YOUKEN_TYPE.PRICE_LOW,
                              );
                            }}
                          >
                            {t('pricing_page.re_estimate.button')}
                          </Button>
                        </Col>
                      )}
                      {estimatePriceData?.hasQuick && (
                        <Col className="re_estimate_condition">
                          <Typography.Title
                            level={5}
                            className="re_estimate_condition_title"
                          >
                            {t('pricing_page.re_estimate.faster')}
                          </Typography.Title>
                          <Button
                            type="primary"
                            onClick={() => {
                              setEnableBackOrder(true);
                              onHandleCalculate(
                                formValue,
                                SUGGEST_YOUKEN_TYPE.FAST_DELIVERY,
                              );
                            }}
                          >
                            {t('pricing_page.re_estimate.button')}
                          </Button>
                        </Col>
                      )}
                    </Row> */}
                  </div>
                )
              ) : (
                <div className="re_estimate_container">
                  {/* <Divider className="form_divider" />
                  <Row className="initial_estimate">
                    <Col span={24}>
                      <Button
                        type="primary"
                        onClick={() => {
                          setEnableBackOrder(false);
                          onHandleCalculate(
                            formValue,
                            SUGGEST_YOUKEN_TYPE.BACK_ORDER,
                          );
                        }}
                      >
                        {t('pricing_page.re_estimate.initial_estimate_result')}
                      </Button>
                    </Col>
                  </Row> */}
                </div>
              )}
              {isTrialMode && estimatePriceData && (
                <CustomerProfile estimatePriceData={estimatePriceData} />
              )}
              <Divider className="form_divider" style={{ maxWidth: 404 }} />
              {!isTrialMode && (
                <Row
                  align="middle"
                  gutter={[16, 0]}
                  className={`${
                    estimatePriceData?.hasCheap && estimatePriceData?.hasQuick
                      ? 'mt-74'
                      : ''
                  } btn_estimate_results`}
                >
                  <Col span={12}>
                    <Button
                      className="w-full confirm_order_button"
                      type="primary"
                      htmlType="submit"
                      onClick={() => onHandleOrder(estimatePriceData)}
                      disabled={estimatePriceData?.totalMoneyExceedAllowed}
                    >
                      {t('pricing_page.estimate_results.confirm_order_button')}
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Link to={PATH_QUOTATION} className="w-full block">
                      <Button
                        className="w-full order_history_button"
                        type="primary"
                        htmlType="submit"
                      >
                        {t(
                          'pricing_page.estimate_results.estimate_history_button',
                        )}
                      </Button>
                    </Link>
                  </Col>
                </Row>
              )}
              {estimatePrice.data && (
                <div className="custom_calculate_button has_estimate">
                  {ButtonSubmit()}
                </div>
              )}
            </React.Fragment>
          )}
        </Col>
      </Row>
      {!estimatePrice.data && (
        <div className="custom_calculate_button no_estimate">
          <Col span={8}>{ButtonSubmit()}</Col>
        </div>
      )}
      {isTrialMode && <TrialNote />}
    </div>
  );
};

export default Pricing;
